/**
 * GuidedTourProvider
 *
 * Global context provider that mounts the universal tour engine at the app root.
 */

'use client';

// Module-scoped claim for tour open events. When multiple
// GuidedTourProvider instances are mounted (e.g. a page-level tour + a
// view-scoped or embedded modal tour), only one provider should open per
// event. The claim is keyed by event object identity so it naturally resets
// on the next dispatch — no timer races. Contextual mounts (embeddedInModal
// or a resolved flow scope) claim synchronously so they always win over
// page-level mounts, which defer their claim one tick.
let __coachOpenClaimedEvent: Event | null = null;

// Guards against repeated `guided-tour-open` dispatches (the capture worker
// retries every ~2s) each spawning a concurrent launch chain that would
// double-click through the same modals. Module-scoped so it survives the
// provider remount that a cross-route `nav` launch action causes (e.g.
// SmartTask flows navigating to /client-zone/dashboard mid-launch).
let __tourLaunchInFlightFlowId: string | null = null;

// sessionStorage handoff for cross-route launches: the re-dispatched
// `guided-tour-open` can fire before the destination route's provider has
// mounted and subscribed, so a nav launch also stashes the open here. The
// first provider whose moduleKey matches claims it on mount.
const PENDING_TOUR_OPEN_KEY = 'ta_pending_tour_open';
const PENDING_TOUR_OPEN_TTL_MS = 60_000;

/**
 * Stash a tour-open request for the next mounted provider whose moduleKey
 * matches — the same handoff the launcher uses for cross-route opens.
 * Non-launcher callers use this to resume a tour across a full page reload
 * (e.g. entering the training sandbox reloads into the demo client).
 */
export function stashPendingTourOpen(params: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(PENDING_TOUR_OPEN_KEY, JSON.stringify({ ...params, __ts: Date.now() }));
  } catch {
    // sessionStorage unavailable — the reload simply won't resume the tour
  }
}

// Marker set while any provider on this page is running a flow — a
// late-mounting provider that consumes a stashed cross-route open checks it
// so the same flow can't spawn a second runner.
let __tourRunnerActiveFlowId: string | null = null;

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useRouter } from 'next/navigation';
import { claimAISurfaceEvent } from '@/lib/ai/aiSurfaceClaims';
import { migrateOldTourProgressKeys } from './migrateProgressKeys';
import { runTourLaunch } from './utils/tourLaunch';
import { useTourConfig, type UseTourConfigResult } from './hooks/useTourConfig';
import { useTourProgress, type UseTourProgressResult } from './hooks/useTourProgress';
import { useTourEngine, type UseTourEngineResult } from './hooks/useTourEngine';
import {
  GUIDED_TOUR_TRAINING_EVENT,
  type GuidedTourTrainingEventDetail,
} from './hooks/useTourVerification';
import type { TourFormat } from './utils/formatResolver';
import {
  OPERATIONS_GUIDED_TOUR_TRAINING_EVENT,
  OPERATIONS_GUIDED_TOUR_FOCUS_EVENT,
  type OperationsGuidedTourTrainingEventDetail,
  type OperationsGuidedTourFocusDetail,
} from '@/lib/operations/guidedTourEvents';
import type {
  TourFlow,
  TourObjective,
  TourStep,
  TourGlobalConfig,
} from '@/config/guided-tours/types';

interface GuidedTourContextValue {
  isOpen: boolean;
  moduleKey: string;
  activeTab: string;
  role: string;
  /** Index presentation mode: 'compact' (mascot + card in corner) or 'overlay' (full-screen welcome). */
  indexMode: 'compact' | 'overlay';
  /** When true, the tour is embedded in a modal — no floating button, auto-opens in chat mode. */
  embeddedInModal: boolean;
  openTour: (params?: { moduleKey?: string; flowId?: string; stepId?: string; objectiveId?: string; format?: TourFormat; launched?: boolean; capture?: boolean }) => void;
  closeTour: () => void;
  toggleTour: () => void;
  config: UseTourConfigResult;
  progress: UseTourProgressResult;
  engine: UseTourEngineResult;
  schema: TourGlobalConfig | null;
  flows: TourFlow[];
  objectives: TourObjective[];
  currentStep: TourStep | null;
  selectedFlow: TourFlow | null;
  selectedObjective: TourObjective | null;
  /**
   * When set, the index is scoped to these flow IDs — used by embedded/modal
   * and context-scoped mounts so they only surface tours relevant to the
   * host component instead of the whole module catalogue.
   */
  scopedFlowIds: string[] | null;
}

const GuidedTourContext = createContext<GuidedTourContextValue | null>(null);

interface GuidedTourProviderProps {
  children: ReactNode;
  defaultModuleKey?: string;
  defaultActiveTab?: string;
  role?: string;
  userId?: string;
  enabled?: boolean;
  onNavigate?: (tab: string) => void;
  /** Index presentation mode: 'compact' (default) or 'overlay' (full-screen welcome). */
  indexMode?: 'compact' | 'overlay';
  /** When true, the tour is embedded in a modal — no floating button, auto-opens in chat mode. */
  embeddedInModal?: boolean;
  /** The flow to auto-select when embeddedInModal is true. */
  autoFlowId?: string;
  /** Tab ids this mount's onNavigate can switch to. Scoped claims are
   *  limited to flows whose target_tab is navigable — flows hosted on
   *  other page tabs fall through to the page-level provider. */
  navigableTabs?: string[];
  /**
   * Scope the flow index (and coach-open priority) to a specific set of
   * flows — e.g. the flows that belong to a modal or designer context.
   * When omitted, embedded mounts default to just the resolved autoFlowId
   * flow; non-contextual mounts show the full module index.
   */
  contextFlowIds?: string[];
}

export function GuidedTourProvider({
  children,
  defaultModuleKey = 'total-operations',
  defaultActiveTab = 'dashboard',
  role = 'all',
  userId,
  enabled = true,
  onNavigate,
  indexMode = 'compact',
  embeddedInModal = false,
  autoFlowId,
  navigableTabs,
  contextFlowIds,
}: GuidedTourProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [moduleKey, setModuleKey] = useState(defaultModuleKey);
  const [activeTab, setActiveTab] = useState(defaultActiveTab);

  // Sync internal activeTab when the prop changes (e.g., user clicks a nav item)
  useEffect(() => {
    setActiveTab(defaultActiveTab);
  }, [defaultActiveTab]);

  // Sync internal moduleKey when the prop changes — e.g. the dashboard swaps
  // its tour module as the active embedded view changes (SmartTask →
  // total-operations). Skipped while a tour is open so an in-flight flow is
  // never yanked out from under the runner.
  useEffect(() => {
    if (!isOpen) {
      setModuleKey(defaultModuleKey);
    }
  }, [defaultModuleKey, isOpen]);

  const config = useTourConfig({ moduleKey, role, enabled });
  const progress = useTourProgress({
    moduleKey,
    userId,
    progressConfig: config.schema?.progress ?? null,
    enabled,
  });

  const onNavigateInternal = useCallback((tab: string) => {
    setActiveTab(tab);
    onNavigate?.(tab);
  }, [onNavigate]);

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const { markFlowActive } = progress;
  const persistFlowActive = useCallback(
    (flowId: string, stepIndex: number, objectiveId?: string | null) => {
      markFlowActive(flowId, stepIndex, objectiveId);
    },
    [markFlowActive]
  );

  // Resolve a flow id tolerantly of legacy coach viewIds: exact match first,
  // then progressively strip leading segments
  // ('finance-banking' → 'banking', 'admin-hub-iam-console' → 'iam-console').
  const resolveFlowById = useCallback(
    (flowId: string | null | undefined): TourFlow | null => {
      if (!flowId || config.flows.length === 0) return null;
      const candidates = [flowId];
      const parts = flowId.split('-');
      if (parts.length > 1) candidates.push(parts.slice(1).join('-'));
      if (parts.length > 2) candidates.push(parts.slice(2).join('-'));
      return (
        candidates
          .map((id) => config.flows.find((f) => f.id === id))
          .find((f) => f !== undefined) ?? null
      );
    },
    [config.flows]
  );

  // Resolve the mount's autoFlowId tolerantly of legacy coach viewIds.
  const resolvedAutoFlow = useMemo(
    () => resolveFlowById(autoFlowId),
    [autoFlowId, resolveFlowById]
  );

  // Contextual mounts scope the index and claim open events before
  // page-level mounts. A view-scoped mount (autoFlowId that resolves to a
  // flow, e.g. the IAM Console view coach) is contextual too — its index
  // only surfaces the view's own flow so a tour never launches against a
  // component that isn't mounted.
  const scopedFlowIds = useMemo(() => {
    const ids =
      contextFlowIds && contextFlowIds.length > 0
        ? contextFlowIds
        : resolvedAutoFlow
          ? [resolvedAutoFlow.id]
          : null;
    if (!ids || !navigableTabs) return ids;
    // Drop flows this view cannot navigate to — a flow whose target_tab
    // lives on a different page tab must fall through to the page-level
    // provider, which can actually switch tabs for it.
    const navigable = ids.filter((id) => {
      const flow = config.flows.find((f) => f.id === id);
      return !flow?.target_tab || navigableTabs.includes(flow.target_tab);
    });
    return navigable.length > 0 ? navigable : null;
  }, [contextFlowIds, resolvedAutoFlow, navigableTabs, config.flows]);
  const isContextual = embeddedInModal || scopedFlowIds !== null;

  const engine = useTourEngine({
    flows: config.flows,
    objectives: config.objectives,
    activeTab,
    initialFlowId: progress.progress.last_active_flow_id,
    initialStepIndex: progress.progress.last_active_step_index ?? 0,
    initialObjectiveId: progress.progress.last_active_objective_id,
    onClose,
    onNavigate: onNavigateInternal,
    persistFlowActive,
  });

  // An open request that arrived while this mount's config was still
  // settling (moduleKey switch or first fetch in flight) is stashed here and
  // replayed once flows land — see the effect after openTour.
  const pendingOpenRef = useRef<{
    moduleKey?: string;
    flowId?: string;
    stepId?: string;
    objectiveId?: string;
    format?: TourFormat;
    launched?: boolean;
    capture?: boolean;
  } | null>(null);

  const router = useRouter();

  const openTour = useCallback(
    (params?: {
      moduleKey?: string;
      flowId?: string;
      stepId?: string;
      objectiveId?: string;
      format?: TourFormat;
      launched?: boolean;
      capture?: boolean;
    }) => {
      const nextModuleKey = params?.moduleKey ?? moduleKey;
      setModuleKey(nextModuleKey);

      if (params?.flowId) {
        // Tolerate legacy view-scoped ids ('admin-hub-iam-console' →
        // 'iam-console') so events aimed at the old coach viewIds still land.
        const resolved = resolveFlowById(params.flowId);

        // When the flow can't be resolved yet because this mount's config is
        // mid-flight (first load, or a moduleKey switch triggered by this very
        // request), stash the request and replay it once flows arrive — a
        // selectFlow call now would silently no-op. Without this, cross-page
        // launches (e.g. SmartTask flows landing on the dashboard provider
        // while it still holds platform-basics flows) drop the open request.
        if (
          !resolved &&
          (config.loading ||
            config.flows.length === 0 ||
            (!!params.moduleKey &&
              (params.moduleKey !== moduleKey || params.moduleKey !== defaultModuleKey)))
        ) {
          pendingOpenRef.current = params;
          setIsOpen(true);
          return;
        }

        // Surface-hosted flows (wizard modals, designers, detail modals)
        // declare `launch` actions that mount their host surface. When this
        // mount doesn't own the flow, run the launch and re-dispatch the open
        // request so the freshly-mounted scoped provider can claim it — the
        // tour then runs inside the surface with its own navigation. The
        // `launched` flag breaks the loop if the surface never mounts: the
        // re-dispatched request falls back to a page-level provider.
        // Headless capture opens can override the launch plan via
        // `capture_launch` (e.g. open a seeded saved form instead of a blank
        // designer) without changing what interactive users get.
        const launchActions =
          params?.capture && resolved?.capture_launch?.length
            ? resolved.capture_launch
            : resolved?.launch;

        // Cross-route handoff: this provider's onNavigate can only switch
        // tabs on the page it is mounted on. When the flow's host surface
        // lives on another route (e.g. a workforce flow opened from the
        // unified dashboard), prepend a nav to the module's declared
        // host_route so the page-level provider there mounts and claims the
        // re-dispatched open. Declared `nav` actions win — flows hosted off
        // their module route (e.g. SmartTask on the dashboard) already carry
        // the right destination.
        const hostRoute = config.moduleConfig?.host_route;
        const hasDeclaredNav = launchActions?.some((a) => a.nav);
        const needsRouteHandoff =
          !!hostRoute &&
          !!resolved?.target_tab &&
          !hasDeclaredNav &&
          typeof window !== 'undefined' &&
          !window.location.pathname.startsWith(hostRoute);
        const effectiveLaunch = needsRouteHandoff
          ? [
              { nav: `${hostRoute}?tab=${encodeURIComponent(resolved!.target_tab!)}` },
              ...(launchActions ?? []),
            ]
          : launchActions;

        if (
          resolved &&
          effectiveLaunch?.length &&
          !scopedFlowIds?.includes(resolved.id) &&
          !params?.launched
        ) {
          // A launch for this flow is already running — its completion
          // re-dispatches the open (launched:true) for the surface's scoped
          // provider to claim. Falling through to selectFlow here would open
          // the tour on this provider prematurely, and the re-dispatch would
          // then reset or collapse it mid-run.
          if (__tourLaunchInFlightFlowId === resolved.id) return;
          const relaunchDetail = { ...params, moduleKey: nextModuleKey, launched: true };
          __tourLaunchInFlightFlowId = resolved.id;
          // A cross-route launch can outlive this mount — the re-dispatch
          // below may fire before the destination provider subscribes, so
          // stash the open where the next matching provider mount claims it.
          if (effectiveLaunch.some((a) => a.nav?.startsWith('/'))) {
            try {
              sessionStorage.setItem(
                PENDING_TOUR_OPEN_KEY,
                JSON.stringify({ ...relaunchDetail, __ts: Date.now() })
              );
            } catch {
              // best-effort — the event re-dispatch still covers same-page mounts
            }
          }
          void runTourLaunch(effectiveLaunch, {
            navigate: (href) => router.push(href),
            moduleKey: nextModuleKey,
          })
            .then(() => {
              window.dispatchEvent(
                new CustomEvent('guided-tour-open', { detail: relaunchDetail })
              );
            })
            .finally(() => {
              if (__tourLaunchInFlightFlowId === resolved.id) {
                __tourLaunchInFlightFlowId = null;
              }
            });
          return;
        }

        // A duplicate `launched` re-dispatch (double launch or a retried open
        // claimed by a scoped mount during the launch) must not reset a tour
        // that is already running this flow.
        if (
          params?.launched &&
          isOpen &&
          resolved?.id &&
          engine.selectedFlowId === resolved.id &&
          engine.viewMode === 'runner'
        ) {
          // Already running this flow — but if it was minimized, surface it.
          if (engine.isMinimized) engine.restore();
          return;
        }

        // Defer flow selection until the next tick so the config has loaded.
        setTimeout(() => {
          engine.selectFlow(resolved?.id ?? params.flowId!, params?.format, params?.stepId);
        }, 0);
      } else if (params?.objectiveId) {
        const objective = config.objectives.find((o) => o.id === params.objectiveId);

        // Mirror the flow path: while config is mid-flight the objective
        // can't be resolved — stash and replay once flows land.
        if (!objective && config.loading) {
          pendingOpenRef.current = params;
          setIsOpen(true);
          return;
        }

        // Cross-route handoff for objectives: this provider's onNavigate can
        // only switch tabs on the page it is mounted on. When the objective's
        // target tab lives on the module's host route and we're elsewhere
        // (e.g. a dashboard provider holding this module's config), stash the
        // open and navigate — the page-level provider there claims it and
        // runs the objective against the right page.
        const hostRoute = config.moduleConfig?.host_route;
        if (
          objective?.target_tab &&
          hostRoute &&
          typeof window !== 'undefined' &&
          !window.location.pathname.startsWith(hostRoute)
        ) {
          try {
            sessionStorage.setItem(
              PENDING_TOUR_OPEN_KEY,
              JSON.stringify({ ...params, moduleKey: nextModuleKey, __ts: Date.now() })
            );
          } catch {
            // best-effort — the nav below still lands on the host route
          }
          router.push(`${hostRoute}?tab=${encodeURIComponent(objective.target_tab)}`);
          return;
        }

        setTimeout(() => {
          engine.selectObjective(params.objectiveId!);
        }, 0);
      } else if (params?.format) {
        engine.setCurrentFormat(params.format);
      }

      setIsOpen(true);
    },
    [engine, moduleKey, defaultModuleKey, resolveFlowById, scopedFlowIds, config.loading, config.flows.length, config.objectives, config.moduleConfig?.host_route, isOpen, router]
  );

  // Keep a stable ref to openTour for the mount-time pending-open claim.
  const openTourRef = useRef(openTour);
  useEffect(() => {
    openTourRef.current = openTour;
  });

  // A cross-route launch stashes its re-open in sessionStorage before
  // navigating — the re-dispatched event can fire before this route's
  // provider subscribes. The first mounted provider whose moduleKey matches
  // claims it; a stale stash expires so an abandoned handoff can't pop a
  // tour long after the fact.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = sessionStorage.getItem(PENDING_TOUR_OPEN_KEY);
      if (!raw) return;
      const detail = JSON.parse(raw) as {
        __ts?: number;
        moduleKey?: string;
        flowId?: string;
        [key: string]: unknown;
      };
      if (typeof detail?.__ts !== 'number' || Date.now() - detail.__ts > PENDING_TOUR_OPEN_TTL_MS) {
        sessionStorage.removeItem(PENDING_TOUR_OPEN_KEY);
        return;
      }
      if (detail.moduleKey && detail.moduleKey !== defaultModuleKey) return;
      sessionStorage.removeItem(PENDING_TOUR_OPEN_KEY);
      const { __ts: _ts, ...params } = detail;
      // A runner for this flow is already open on a sibling provider (the
      // re-dispatch landed before this mount) — don't spawn a second one.
      if (params.flowId && __tourRunnerActiveFlowId === params.flowId) return;
      openTourRef.current(params as Parameters<typeof openTour>[0]);
    } catch {
      // malformed stash — ignore
    }
  }, [defaultModuleKey]);

  // Advertise the flow this mount is running so a stashed cross-route open
  // consumed by a later-mounting sibling can't spawn a second runner.
  useEffect(() => {
    if (isOpen && engine.viewMode === 'runner' && engine.selectedFlowId) {
      __tourRunnerActiveFlowId = engine.selectedFlowId;
    } else if (__tourRunnerActiveFlowId === engine.selectedFlowId) {
      __tourRunnerActiveFlowId = null;
    }
  }, [isOpen, engine.viewMode, engine.selectedFlowId]);

  // Replay a stashed open request once this mount's config has settled.
  // The stash is cleared before replaying so a flow that still can't be
  // resolved simply degrades to the index — no replay loop.
  useEffect(() => {
    const pending = pendingOpenRef.current;
    if (!pending || config.loading) return;
    pendingOpenRef.current = null;
    openTour(pending);
  }, [config.loading, config.flows, openTour]);

  const closeTour = useCallback(() => {
    pendingOpenRef.current = null;
    engine.close();
    setIsOpen(false);
  }, [engine]);

  const toggleTour = useCallback(() => {
    if (isOpen) {
      closeTour();
    } else {
      openTour();
    }
  }, [closeTour, isOpen, openTour]);

  // Migrate old localStorage progress keys to new prefix (runs once per browser).
  useEffect(() => {
    migrateOldTourProgressKeys();
  }, []);

  // Bridge legacy operations/finance training events into the universal
  // guided-tour-training channel so sandbox/demo actions verify steps.
  useEffect(() => {
    const handleLegacyEvent = (event: Event) => {
      const detail = (event as CustomEvent<OperationsGuidedTourTrainingEventDetail>).detail;
      if (!detail?.type) {
        return;
      }

      window.dispatchEvent(
        new CustomEvent<GuidedTourTrainingEventDetail>(GUIDED_TOUR_TRAINING_EVENT, {
          detail: {
            type: detail.type,
            occurredAt: detail.occurredAt,
            stepId: null,
            metadata: {
              ...(detail.metadata ?? {}),
              route: detail.route ?? null,
              workItemId: detail.workItemId ?? null,
              itemType: detail.itemType ?? null,
            },
          },
        })
      );
    };

    window.addEventListener(OPERATIONS_GUIDED_TOUR_TRAINING_EVENT, handleLegacyEvent as EventListener);
    return () => {
      window.removeEventListener(OPERATIONS_GUIDED_TOUR_TRAINING_EVENT, handleLegacyEvent as EventListener);
    };
  }, []);

  // Bridge focus_target changes to the legacy operations focus event so that
  // components like ContactManagementHub and DynamicOperationalView can switch
  // tabs and apply CSS ring highlights for tour targets that don't have a
  // data-tour-id in the DOM (e.g. stakeholders-tab-*, service-operations-*).
  useEffect(() => {
    if (!isOpen || engine.viewMode !== 'runner' || !engine.currentStep?.focus_target) {
      return;
    }

    window.dispatchEvent(
      new CustomEvent<OperationsGuidedTourFocusDetail>(OPERATIONS_GUIDED_TOUR_FOCUS_EVENT, {
        detail: { target: engine.currentStep.focus_target },
      })
    );
  }, [isOpen, engine.viewMode, engine.currentStep?.focus_target]);

  // Keyboard shortcut to open/close tour with `?`.
  // Skipped in modal-embedded mode (the modal provides its own trigger).
  // Contextual mounts claim the keypress synchronously so only the innermost
  // provider toggles — page-level mounts defer one tick and back off.
  useEffect(() => {
    if (embeddedInModal) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === '?' &&
        !(event.target instanceof HTMLInputElement ||
          event.target instanceof HTMLTextAreaElement)
      ) {
        event.preventDefault();
        const toggle = () => {
          if (__coachOpenClaimedEvent === event) return;
          __coachOpenClaimedEvent = event;
          toggleTour();
        };
        if (isContextual) {
          toggle();
        } else {
          setTimeout(toggle, 0);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleTour, embeddedInModal, isContextual]);

  // When embedded in a modal, pre-select the auto flow's chat context so it's
  // ready when the user opens the coach via the "AI Training Coach" button.
  // We do NOT auto-open — the user must click the button in the footer AI
  // sub-menu (which dispatches 'ai-coach-open', handled below).
  useEffect(() => {
    if (!embeddedInModal) return;
    if (resolvedAutoFlow) {
      engine.selectChat(resolvedAutoFlow.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embeddedInModal, resolvedAutoFlow?.id]);

  // Custom event listener for external tour open requests.
  // Allows components outside the provider to trigger the tour via:
  //   window.dispatchEvent(new CustomEvent('guided-tour-open', { detail: { moduleKey, flowId, stepId } }))
  // Scoped mounts claim requests that fall inside their scope synchronously:
  // targeted flow ids they own, or — for view-scoped (non-modal) mounts —
  // untargeted index opens. Everything else defers one tick; an unclaimed
  // event falls back to any mount so nothing is dropped.
  useEffect(() => {
    const handleOpenTourRequest = (event: Event) => {
      const detail = (event as CustomEvent<{ moduleKey?: string; flowId?: string; stepId?: string; objectiveId?: string; launched?: boolean; capture?: boolean }>).detail;

      const inScope = (() => {
        if (!isContextual) return false;
        if (!detail?.flowId && !detail?.objectiveId) return !embeddedInModal;
        const flow = resolveFlowById(detail?.flowId);
        return !!flow && !!scopedFlowIds?.includes(flow.id);
      })();

      const open = () => {
        if (__coachOpenClaimedEvent === event) {
          // Another mount claimed this request — collapse our panel so two
          // tour UIs can never be open at once (e.g. a page-level provider
          // that claimed an earlier dispatch while our flows were still
          // loading would otherwise leave its index overlaying the runner).
          if (isOpen) closeTour();
          return;
        }
        __coachOpenClaimedEvent = event;
        openTour(detail ?? {});
      };

      // Prefer the mount that can actually host the request. A mount whose
      // module doesn't match the request's and can't resolve the flow yet
      // (e.g. platform-basics claiming a workforce open) backs off one extra
      // tick so a matching mount claims first — an unmatched claim otherwise
      // swallows the flowId and degrades to a bare index open. If no mount
      // can host, the delayed claim still lands somewhere.
      const targeted = !!(detail?.flowId || detail?.objectiveId);
      const moduleMismatch =
        !!detail?.moduleKey && detail.moduleKey !== moduleKey && detail.moduleKey !== defaultModuleKey;
      const canHostNow =
        !targeted || !moduleMismatch || !!resolveFlowById(detail?.flowId ?? detail?.objectiveId ?? null);

      if (inScope) {
        open();
      } else {
        setTimeout(open, canHostNow ? 0 : 60);
      }
    };

    window.addEventListener('guided-tour-open', handleOpenTourRequest as EventListener);
    return () => window.removeEventListener('guided-tour-open', handleOpenTourRequest as EventListener);
  }, [openTour, closeTour, isOpen, isContextual, embeddedInModal, scopedFlowIds, resolveFlowById, defaultModuleKey, moduleKey]);

  // Listen for the legacy 'ai-coach-open' event (dispatched by "AI Training Coach"
  // buttons in ClientZoneFooter, module pages, modal footers, etc.).
  // Opens the tour in chat mode — the button is for users who are stuck and want
  // conversational help, not a step-by-step tour.
  // Claims are keyed by event identity so only one provider opens per dispatch.
  // Contextual mounts (embedded modals, scoped designers) claim synchronously;
  // page-level mounts defer one tick so the innermost context always wins.
  useEffect(() => {
    const handleCoachOpen = (event: Event) => {
      const open = () => {
        if (__coachOpenClaimedEvent === event) return;
        __coachOpenClaimedEvent = event;

        setIsOpen(true);
        // Prefer the currently selected flow, then the mount's auto flow;
        // otherwise open module-level chat.
        const chatFlowId = engine.selectedFlow?.id ?? resolvedAutoFlow?.id;
        if (chatFlowId) {
          engine.selectChat(chatFlowId);
        } else {
          engine.selectChat();
        }
      };

      if (isContextual) {
        open();
      } else {
        setTimeout(open, 0);
      }
    };

    window.addEventListener('ai-coach-open', handleCoachOpen);
    return () => window.removeEventListener('ai-coach-open', handleCoachOpen);
  }, [engine, isContextual, resolvedAutoFlow]);

  // Listen for 'ai-assistant-open' — the module AI Assistant is the same
  // unified chat surface (selectChat with no flow = module-level context).
  // Claims go through the shared aiSurfaceClaims registry so legacy per-page
  // floating assistants can detect the claim and back off instead of
  // opening a second chat panel.
  // Contextual mounts claim synchronously; the platform-basics provider and
  // module-mismatched mounts defer so the most relevant mount wins.
  useEffect(() => {
    const handleAssistantOpen = (event: Event) => {
      const detail = (event as CustomEvent<{ moduleKey?: string; local?: boolean }>).detail;

      // detail.local means the dispatcher is opening its own local assistant
      // surface (e.g. WorkItemDetailModal's contextual assistant) — the
      // unified surface must not claim it.
      if (detail?.local) return;

      const open = () => {
        if (!claimAISurfaceEvent(event)) {
          // Another mount claimed this request — collapse our panel so two
          // AI surfaces are never open at once.
          if (isOpen) closeTour();
          return;
        }
        setIsOpen(true);
        engine.selectChat();
      };

      const moduleMismatch =
        !!detail?.moduleKey && detail.moduleKey !== moduleKey && detail.moduleKey !== defaultModuleKey;
      const isPlatform = moduleKey === 'platform-basics' && defaultModuleKey === 'platform-basics';

      if (isContextual) {
        open();
      } else if (moduleMismatch || isPlatform) {
        setTimeout(open, 60);
      } else {
        setTimeout(open, 0);
      }
    };

    window.addEventListener('ai-assistant-open', handleAssistantOpen);
    return () => window.removeEventListener('ai-assistant-open', handleAssistantOpen);
  }, [engine, isContextual, moduleKey, defaultModuleKey, isOpen, closeTour]);

  const value = useMemo<GuidedTourContextValue>(
    () => ({
      isOpen,
      moduleKey,
      activeTab,
      role,
      openTour,
      closeTour,
      toggleTour,
      config,
      progress,
      engine,
      schema: config.schema,
      flows: config.flows,
      objectives: config.objectives,
      currentStep: engine.currentStep,
      selectedFlow: engine.selectedFlow,
      selectedObjective: engine.selectedObjective,
      indexMode,
      embeddedInModal,
      scopedFlowIds,
    }),
    [isOpen, moduleKey, activeTab, role, openTour, closeTour, toggleTour, config, progress, engine, indexMode, embeddedInModal, scopedFlowIds],
  );

  return (
    <GuidedTourContext.Provider value={value}>
      {children}
    </GuidedTourContext.Provider>
  );
}

export function useGuidedTour(): GuidedTourContextValue {
  const context = useContext(GuidedTourContext);
  if (!context) {
    throw new Error('useGuidedTour must be used within a GuidedTourProvider');
  }
  return context;
}

export { GuidedTourContext };
